import { CATEGORY_LABELS, CATEGORY_ORDER, MAX_FACET_LABEL } from './constants';
import type { Facet, FacetOption, FilterCategory } from './types';

export type FacetAddGroup = { category: FilterCategory; label: string; options: FacetAddOption[] };
export type FacetAddOption = { label: string; value: string };

export const automaticallyIncludesAllCompareValues = (facet: Facet): boolean =>
  facet.category === 'demographic' || facet.category === 'quota';

/** Truncate with an ellipsis (mirrors Cardify's `truncate`). */
export const truncate = (text: string, max: number = MAX_FACET_LABEL): string =>
  text.length > max ? `${text.slice(0, max - 1)}…` : text;

/** Look up a facet by its stable id. */
export const facetById = (facets: Facet[], id: string): Facet | undefined =>
  facets.find((facet) => facet.id === id);

/** Case-insensitive substring filter over an option's label and value. */
export const filterOptionsByQuery = (options: FacetOption[], query: string): FacetOption[] => {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return options;
  return options.filter(
    ({ label, value }) =>
      label.toLowerCase().includes(trimmed) || value.toLowerCase().includes(trimmed)
  );
};

/**
 * Build grouped "+ Add …" options in fixed CATEGORY_ORDER, excluding facets
 * already in use and any that fail `predicate` (filterable vs comparable).
 * Empty groups are dropped. Returns data only — the component attaches the
 * trip-attribute tooltip to the group header.
 */
export const buildFacetGroups = (
  facets: Facet[],
  {
    excludeIds = new Set<string>(),
    predicate,
  }: { excludeIds?: Set<string>; predicate: (facet: Facet) => boolean }
): FacetAddGroup[] =>
  CATEGORY_ORDER.map((category) => ({
    category,
    label: CATEGORY_LABELS[category],
    options: facets
      .filter(
        (facet) => facet.category === category && predicate(facet) && !excludeIds.has(facet.id)
      )
      .map((facet) => ({
        label: facet.category === 'question' ? facet.label : truncate(facet.label),
        value: facet.id,
      })),
  })).filter((group) => group.options.length > 0);
